import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { KeyFacts } from '../../components/LessonUI';

const comparisonCode = `// Comparing the evolution of asynchronous syntax

// 1. The Promise Chain (Cleaner but still nested/split logic)
function loadUserWithPromises() {
    return fetchUser(1)
        .then(user => fetchProfile(user.id))
        .then(profile => renderProfile(profile))
        .catch(err => console.error("Chain failed", err));
}

// 2. The Async/Await equivalent (Linear and intuitive)
async function loadUserWithAsync() {
    try {
        const user = await fetchUser(1);      // Function execution "pauses" here
        const profile = await fetchProfile(user.id);
        return renderProfile(profile);
    } catch (err) {
        console.error("Async call failed", err);
    }
}`;

const keywordBehaviorCode = `// Exploring the behavior of 'async' and 'await' keywords

// The 'async' keyword ensures the return value is always a Promise
async function getConstantValue() {
    return 42; // Implicitly returns Promise.resolve(42)
}

// Consuming an async function
getConstantValue().then(val => console.log(val));

// The 'await' keyword unwraps the Promise value
async function performCalculation() {
    console.log("A: Start");
    
    // Control is yielded back to the event loop here
    const value = await getConstantValue(); 
    
    console.log("B: Value received", value);
    return value + 10;
}

console.log("C: External code continues while 'performCalculation' is paused");`;

const sequentialVsParallelCode = `// Performance Optimization: Sequential vs Parallel execution

//  ANTI-PATTERN: Sequential requests (Total time: Sum of all requests)
async function loadDashboardSequential() {
    const user = await fetchUser();         // Wait...
    const config = await fetchConfig();     // Wait...
    const posts = await fetchPosts();       // Wait...
    return { user, config, posts };
}

// BEST PRACTICE: Parallel requests (Total time: The longest single request)
async function loadDashboardParallel() {
    // Initiate all promises immediately
    const userPromise = fetchUser();
    const configPromise = fetchConfig();
    const postsPromise = fetchPosts();

    // Await all outcomes concurrently
    const [user, config, posts] = await Promise.all([
        userPromise,
        configPromise,
        postsPromise
    ]);
    
    return { user, config, posts };
}`;

const advancedPatternsCode = `// Robust patterns for Async/Await production code

// 1. Retry logic with exponential backoff
async function resilientFetch(url, limit = 3) {
    for (let i = 0; i < limit; i++) {
        try {
            return await fetch(url).then(r => r.json());
        } catch (err) {
            const delay = Math.pow(2, i) * 1000;
            console.warn(\`Attempt \${i+1} failed, retrying in \${delay}ms...\`);
            await new Promise(res => setTimeout(res, delay));
        }
    }
    throw new Error("Maximum retries reached");
}

// 2. The "Early Return" error handling pattern
async function processBatch(items) {
    const results = await Promise.allSettled(items.map(item => process(item)));
    
    // Separating successes from failures cleanly
    const successful = results.filter(r => r.status === 'fulfilled');
    const failed = results.filter(r => r.status === 'rejected');
    
    return { successful, failed };
}`;

const JSAsyncAwait = () => {
    return (
        <div className="premium-course-content">
            <div className="content-hero">
                <div className="hero-badge">Asynchronous JavaScript</div>
                <h1>Async/Await: The Syntactic Perfection</h1>
                <p className="hero-lead">
                    Master the industry-standard syntax for managing asynchronous operations. Learn how
                    Async/Await flattens complex Promise chains into readable, maintainable, and
                    structurally synchronous-looking code.
                </p>
            </div>

            <div className="content-section">
                <h2>The Evolution: Behind the Syntactic Sugar</h2>
                <p>
                    Async/Await, introduced in ES2017 (ES8), represents the pinnacle of asynchronous control flow
                    patterns in JavaScript. While it is often described as "syntactic sugar" over Promises, it is
                    architecturally powered by the combination of Promises and Generator functions. In the past,
                    developers had to rely on complex Library-based generator runners to achieve this linear
                    flow, but the language now supports it natively. By making asynchronous code look like
                    standard synchronous logic, it drastically reduces the cognitive load required to understand
                    time-dependent operations. This shift has fundamentally changed how we architect large-scale
                    applications, moving away from fragmented logic into unified, logical procedures.
                </p>
                <p>
                    When a function is marked with the `async` keyword, it gains two magical properties: it
                    automatically returns a Promise, and it enables the use of the `await` keyword within its
                    body. If you return a primitive value like a string or number, JavaScript automatically
                    wraps it in `Promise.resolve()`. This ensures a "contractual consistency" where callers
                    can always expect a Promise-compliant object from any async function. This predictability
                    is essential for building composable systems where functions are chained or passed as
                    first-class citizens. Furthermore, it allows the JavaScript engine to optimize the
                    execution state of these functions, preparing them for the specialized "suspension"
                    behavior that defines the modern async experience.
                </p>

                <CodeBlock code={comparisonCode} language="javascript" />
            </div>

            <div className="content-section">
                <h2>The Suspension Mechanism: How Await Yields Control</h2>
                <p>
                    The `await` keyword is perhaps the most misunderstood element of modern JavaScript. It does
                    not "stop" the world or freeze the browser; instead, it **yields control** back to the
                    Event Loop. When the engine encounters an `await`, it packs up the current function's
                    execution contextincluding local variables and the instruction pointerand sets it aside
                    while the Promise settles. This allows the main thread to remain responsive, handling user
                    input, animations, and other Macrotasks in the meantime. Once the awaited Promise resolves,
                    the function's context is restored from the Microtask Queue, and execution resumes
                    exactly where it left off. This "pause-and-resume" cycle is what allows for the structural
                    elegance of synchronous code without the catastrophic performance penalty of thread blocking.
                </p>
                <p>
                    Architecturally, using `await` is superior to `.then()` because it handles the stack trace
                    more naturally. In a standard Promise chain, the original "context" of the call is often
                    lost as execution jumps between handler callbacks, making debugging significantly harder.
                    With `async/await`, the debugger can reconstruct the execution path much like a synchronous
                    stack, providing clear insights into how an error occurred. This "context awareness" is a
                    massive win for developer productivity, especially in complex enterprise applications where
                    asynchronous operations might go ten levels deep. By maintaining the logical continuity of
                    a function body, we preserve the developer's mental model of how the application state
                    should evolve over time.
                </p>

                <CodeBlock code={keywordBehaviorCode} language="javascript" />
            </div>

            <div className="content-section">
                <h2>Concurrency Strategy: Sequential vs Parallel</h2>
                <p>
                    A common architectural pitfall is the "sequential await trap," where developers inadvertently
                    chain multiple `await` statements that have no dependency on each other. If you are loading
                    a user's posts, their friends list, and their settings, awaiting them one-by-one results
                    in a total wait time equal to the sum of all response times. This essentially brings back
                    the inefficiencies of synchronous code. By instead initiating the Promises immediately
                    without `await` and then using `Promise.all` to wait for the collective result, you
                    trigger parallel execution. This ensures that the total wait time is only as long as the
                    slowest individual request, effectively maximizing the available network bandwidth.
                    Understanding when to use sequential flow (for dependencies) versus parallel flow (for
                    independent data) is the hallmark of a senior engineer.
                </p>
                <p>
                    However, parallel execution isn't always the right answer, particularly when dealing
                    with resource-intensive operations or rate-limited APIs. If you have 1,000 files to
                    upload, using `Promise.all` to trigger them all at once might overwhelm the user's
                    network or crash the browser's memory. In these cases, a "sliding window" or batching
                    approach is required, where you process 5 or 10 items at a time. This requires a
                    nuanced combination of `async/await` and loops, where you selectively await the
                    completion of chunks. This level of control over concurrency allows for high-performance
                    data processing while maintaining system stability and providing a smooth user
                    experience. Transitioning from "just making it work" to "optimizing for throughput"
                    is where the power of this syntax truly shines.
                </p>

                <CodeBlock code={sequentialVsParallelCode} language="javascript" />
            </div>

            <div className="content-section">
                <h2>Advanced Error Handling and Resilience</h2>
                <p>
                    One of the most praised aspects of `async/await` is its symmetry with synchronous
                    `try/catch` error handling. Because the syntax flattens the execution flow, you can wrap
                    multiple asynchronous operations in a single `try` block and handle any aggregate
                    failure in the associated `catch`. This is significantly more intuitive than managing
                    error propagation through multiple `.catch()` callbacks in a Promise chain.
                    The `finally` block also becomes incredibly useful for state cleanupsuch as resetting a
                    loading flag or closing a socketensuring that your application doesn't get stuck in an
                    invalid state. By leveraging these standard control structures, we bring "industrial-strength"
                    reliability to the notoriously unpredictable environment of the web.
                </p>
                <p>
                    In production environments, simply "catching" an error isn't enough; we need resilience
                    strategies like automatic retries and circuit breakers. Using `async/await` and standard
                    loops, implementing retry logic with exponential backoff becomes a trivial 5-line
                    pattern. You can't achieve this level of readability with raw Promises without resorting
                    to complex recursion. Furthermore, for operations where partial success is acceptable,
                    using `await Promise.allSettled()` allows you to inspect every outcome individually
                    without a single rejection crashing the whole set. This granular control over failure
                    states ensures that your application can gracefully degrade when a secondary service
                    is down, rather than failing completely. These patterns are essential for building
                    professional, reliable digital products.
                </p>

                <CodeBlock code={advancedPatternsCode} language="javascript" />
            </div>

            <div className="content-section">
                <h2>Engineering Best Practices</h2>
                <p>
                    Always prioritize `async/await` for any logic that involves sequential asynchronous steps,
                    as it is objectively more readable and easier to debug than Promise chains. Remember that
                    `await` is only valid inside an `async` function, though modern environments now support
                    "Top-level await" in modules, which simplifies initialization code significantly. Be wary
                    of using `await` inside a `.forEach()` loop; it won't behave as you expect because
                    `.forEach` is not "async-aware" and will fire all iterations essentially in parallel
                    without waiting. Instead, use a standard `for...of` loop if you need sequential
                    processing, or `Promise.all` if you want parallel execution. By adhering to these
                    structural standards, you ensure that your code remains accessible to teammates and
                    robust across different JavaScript runtimes.
                </p>

                <KeyFacts><ul>
                        <li>**Readability:** Flattens Promise chains into linear, logical flows.</li>
                        <li>**Yielding:** `await` tells the engine to set the function aside without blocking.</li>
                        <li>**Concurrency:** Use `Promise.all` to avoid the "Sequential Await Trap."</li>
                        <li>**Reliability:** Use `try/catch/finally` just like synchronous code.</li>
                        <li>**Consistency:** Async functions *always* return a Promise.</li>
                        <li>**Observation:** Debuggers can provide cleaner, more accurate stack traces.</li>
                        <li>**Resilience:** Easily implement retries and batching with standard loops.</li>
                    </ul></KeyFacts>
            </div>

        </div>
    );
};

export default JSAsyncAwait;

